// File: rules
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import type { Option, Rule, SubOption } from './carWizardTypes';

export type Selections = Record<string, string>; // stepId -> optionId

export interface EffectState {
  hiddenOptions: Set<string>; // "<stepId>:<optionId>"
  disabledOptions: Set<string>; // "<stepId>:<optionId>"
  hiddenSteps: Set<string>;
  disabledSteps: Set<string>;
  hiddenScreens: Set<string>;
  forcedOptions: Map<string, string>; // stepId -> optionId
  // stepId -> forced variant, applied only while that option is selected.
  forcedSubOptions: Map<string, { optionId: string; subOptionId: string }>;
}

export const optionKey = (stepId: string, optionId: string) => `${stepId}:${optionId}`;

export const evaluate = (rules: Rule[] = [], selections: Selections): EffectState => {
  const out: EffectState = {
    hiddenOptions: new Set(),
    disabledOptions: new Set(),
    hiddenSteps: new Set(),
    disabledSteps: new Set(),
    hiddenScreens: new Set(),
    forcedOptions: new Map(),
    forcedSubOptions: new Map(),
  };

  for (const rule of rules) {
    const matches = rule.when.selections.every((sel) => {
      const userPick = selections[sel.stepId];
      return userPick !== undefined && sel.optionIdIn.includes(userPick);
    });
    if (!matches) continue;

    for (const eff of rule.then) {
      switch (eff.type) {
        case 'hideOption':
          out.hiddenOptions.add(optionKey(eff.targetStepId, eff.targetOptionId));
          break;
        case 'disableOption':
          out.disabledOptions.add(optionKey(eff.targetStepId, eff.targetOptionId));
          break;
        case 'hideStep':
          out.hiddenSteps.add(eff.targetStepId);
          break;
        case 'disableStep':
          out.disabledSteps.add(eff.targetStepId);
          break;
        case 'hideScreen':
          out.hiddenScreens.add(eff.targetScreenId);
          break;
        case 'forceOption':
          out.forcedOptions.set(eff.targetStepId, eff.targetOptionId);
          break;
        case 'forceSubOption':
          out.forcedSubOptions.set(eff.targetStepId, { optionId: eff.targetOptionId, subOptionId: eff.targetSubOptionId });
          break;
      }
    }
  }
  return out;
};

/**
 * Resolve the active coupled sub-option for an option whose sub-options follow
 * another step's selection (doc §4). Returns null if not coupled / no match.
 */
export const resolveActiveSubOption = (
  option: Option | null,
  selections: Selections
): SubOption | null => {
  if (!option?.subOptionsLinkedToStepId) return null;
  const linkedSel = selections[option.subOptionsLinkedToStepId];
  if (!linkedSel) return null;
  return option.subOptions?.find((s) => s.linkedOptionId === linkedSel) ?? null;
};

/** The "fixed" variants of a coupled option = sub-options with no linkedOptionId. */
export const fixedSubOptions = (option: Option | null): SubOption[] =>
  (option?.subOptions || []).filter((s) => !s.linkedOptionId);
